import {
  DrawerContent,
  LoadingCircle,
  FormGroup,
  DrawerActions,
  Checkbox,
  Button,
  ButtonVariant,
} from "@industriousoffice/react-components";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Account,
  AccountSignature,
  InvoicingSystemTypes,
  ZuoraPaymentMethodTypes,
  Location,
} from "@/api/opb/opbTypes";
import { SepaCountries } from "@/utils/location";
import { PaymentDrawerTitle as DrawerTitle } from "./PaymentDrawerTitle";
import {
  PaymentFormInput as FormInput,
  PaymentFormSelect as FormSelect,
} from "./PaymentFormInput";
import { PaymentDrawer as Drawer } from "./PaymentDrawer";
import {
  SepaAccountSchema,
  SepaAccountSchemaType,
} from "../schemas/SepaAccountSchema";
import { encrypt } from "../utils/encrypt";
import { FormPaymentMethodType } from "../types";
import StripePaymentMethodDrawerForm from "./StripePaymentMethodDrawerForm";

import { usePaymentSetup } from "../usePaymentSetup";

interface AddSepaAccountDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  isLoading?: boolean;
  accountSignature?: AccountSignature;
  account?: Account;
  isFirstPaymentMethod?: boolean;
  callbackData?: object;
  redirectUrl?: string;
  accountLocation?: Location;
}

const defaultFormData: SepaAccountSchemaType = {
  bankAccountNumber: "",
  bankAccountName: "",
  field_streetNumber: "",
  field_streetName: "",
  field_email: "",
  field_postalCode: "",
  field_state: "",
  field_city: "",
  field_country: "",

  field_passthrough4: "", // nickname
  field_passthrough5: "", // redirect url
  field_passthrough6: "", // callback data
};

export function AddSepaAccountDrawer({
  isOpen,
  onClose,
  isLoading = false,
  accountSignature,
  account,
  isFirstPaymentMethod = false,
  callbackData,
  redirectUrl,
  accountLocation,
}: AddSepaAccountDrawerProps) {
  const { t } = useTranslation("paymentMethods");
  const [isDefaultPaymentChecked, setIsDefaultPaymentChecked] =
    useState(isFirstPaymentMethod);

  const {
    control,
    reset,
    handleSubmit,
    formState: { isDirty, isValid },
    watch,
  } = useForm<SepaAccountSchemaType>({
    mode: "onSubmit",
    reValidateMode: "onChange",
    values: defaultFormData,
    defaultValues: defaultFormData,
    resolver: yupResolver(SepaAccountSchema),
  });

  const watchBankAccountNumber = watch("bankAccountNumber");
  const watchBankAccountName = watch("bankAccountName");

  useEffect(() => {
    setIsDefaultPaymentChecked(isFirstPaymentMethod);
  }, [isFirstPaymentMethod]);

  const encryptedValues = useMemo<string>(() => {
    const valuesToEncrypt = `#${watchBankAccountNumber}#${watchBankAccountName}`;

    try {
      const val = encrypt(valuesToEncrypt, accountSignature?.publicKey || "");
      return val;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      return "";
    }
  }, [
    watchBankAccountName,
    watchBankAccountNumber,
    accountSignature?.publicKey,
  ]);

  const setup = usePaymentSetup({
    requiresDefaultConfirmation: Boolean(
      account?.autopay && isDefaultPaymentChecked && !isFirstPaymentMethod
    ),
    isOpen,
    isDirty: isDirty || isDefaultPaymentChecked !== isFirstPaymentMethod,
    isValid: isValid && accountSignature != null && encryptedValues.length > 0,
    validate: async () => {
      let valid = false;
      await handleSubmit(() => {
        valid = true;
      })();
      return valid && accountSignature != null && encryptedValues.length > 0;
    },
    onClose,
    onReset: () => {
      reset(defaultFormData);
      setIsDefaultPaymentChecked(isFirstPaymentMethod);
    },
  });
  return (
    <Drawer className="z-10" isOpen={isOpen} onClose={setup.requestClose}>
      {setup.discardDialog}
      <DrawerTitle
        className="pb-4"
        title={t("Add {{scheme}} account", { scheme: "SEPA" })}
        onClose={setup.requestClose}
      />
      {account?.invoicingSystemType === InvoicingSystemTypes.StripeBilling ? (
        <StripePaymentMethodDrawerForm
          accountId={account?._id}
          paymentType={ZuoraPaymentMethodTypes.Sepa}
          currencyIsoCode={accountLocation?.currencyCode}
          isDefaultPaymentChecked={isDefaultPaymentChecked}
          setIsDefaultPaymentChecked={setIsDefaultPaymentChecked}
        />
      ) : (
        <form
          name="SepaPayment2Form"
          action={accountSignature?.paymentPageUrl}
          method="post"
          onSubmit={setup.onSubmit}
          noValidate
          className="payment-add-form payment-bank-add-form"
          aria-busy={setup.isSubmitting}
          id="add-sepa-form"
        >
          {isLoading ? (
            <div className="justify-center items-center flex pt-4">
              <LoadingCircle spinnerSize={30} spinnerColor="#013e3f" />
            </div>
          ) : (
            <>
              <DrawerContent>
                <input type="hidden" name="method" value="submitPage" />
                <input
                  type="hidden"
                  name="field_bankTransferType"
                  value="SEPA"
                />
                <input
                  type="hidden"
                  name="id"
                  value={accountSignature?.paymentPageId || ""}
                />
                <input
                  type="hidden"
                  name="checkDuplicated"
                  id="checkDuplicated"
                  value=""
                />
                <input
                  type="hidden"
                  name="captchaRequired"
                  id="captchaRequired"
                  value=""
                />
                <input
                  type="hidden"
                  name="tenantId"
                  value={accountSignature?.tenantId}
                />
                <input
                  type="hidden"
                  name="token"
                  value={accountSignature?.token}
                />
                <input
                  type="hidden"
                  name="signature"
                  value={accountSignature?.signature}
                />
                <input
                  type="hidden"
                  name="host"
                  value={window.location.origin}
                />
                <input
                  type="hidden"
                  name="field_accountId"
                  value={account?.zuoraId}
                />
                <input
                  type="hidden"
                  name="paymentGateway"
                  value={account?.paymentGateway}
                />
                <input
                  type="hidden"
                  name="field_key"
                  value={accountSignature?.publicKey}
                />
                <input type="hidden" name="field_style" value="iframe" />
                <input
                  type="hidden"
                  name="encrypted_fields"
                  value="#field_bankAccountNumber#field_bankAccountName"
                />
                <input
                  type="hidden"
                  name="encrypted_values"
                  value={encryptedValues}
                />
                <input
                  type="hidden"
                  name="field_passthrough1"
                  value={account?._id}
                />
                <input
                  type="hidden"
                  name="field_passthrough2"
                  value={FormPaymentMethodType.SEPA}
                />
                <input
                  type="hidden"
                  name="field_passthrough3"
                  value={isDefaultPaymentChecked ? "true" : "false"}
                />
                <input
                  type="hidden"
                  name="field_passthrough5"
                  value={redirectUrl}
                />
                <input
                  type="hidden"
                  name="field_passthrough6"
                  value={JSON.stringify(callbackData)}
                />
                <div className="payment-bank-fields">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <FormGroup className="payment-form-group">
                      <FormInput
                        control={control}
                        name="bankAccountNumber"
                        rules={{ required: true }}
                        label={t("Account Number / IBAN")}
                        isRequired
                        data-testid="add_sepa-routing_number-input"
                        placeholder="FR1420041010050500013M02606"
                      />
                    </FormGroup>

                    <FormGroup className="payment-form-group">
                      <FormInput
                        control={control}
                        name="bankAccountName"
                        rules={{ required: true }}
                        label={t("Account holder name")}
                        isRequired
                        data-testid="add_sepa-bank_account_name-input"
                      />
                    </FormGroup>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <FormGroup className="payment-form-group sm:col-span-2">
                      <FormInput
                        control={control}
                        name="field_email"
                        rules={{ required: true }}
                        label={t("Email")}
                        isRequired
                        data-testid="add_sepa-email-input"
                      />
                    </FormGroup>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <FormGroup className="payment-form-group sm:col-span-1">
                      <FormInput
                        control={control}
                        name="field_streetNumber"
                        rules={{ required: true }}
                        label={t("Street number")}
                        isRequired
                        data-testid="add_sepa-street-number-input"
                      />
                    </FormGroup>
                    <FormGroup className="payment-form-group sm:col-span-2">
                      <FormInput
                        control={control}
                        name="field_streetName"
                        rules={{ required: true }}
                        label={t("Street name")}
                        isRequired
                        data-testid="add_sepa-street-name-input"
                      />
                    </FormGroup>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <FormGroup className="payment-form-group sm:col-span-1">
                      <FormInput
                        control={control}
                        name="field_postalCode"
                        rules={{ required: true }}
                        label={t("Postal code")}
                        isRequired
                        data-testid="add_sepa-postal_code-input"
                      />
                    </FormGroup>
                    <FormGroup className="payment-form-group sm:col-span-2">
                      <FormInput
                        control={control}
                        name="field_city"
                        rules={{ required: true }}
                        label={t("City")}
                        isRequired
                        data-testid="add_sepa-city-input"
                      />
                    </FormGroup>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <FormGroup className="payment-form-group">
                      <FormSelect
                        control={control}
                        name="field_country"
                        options={Object.values(SepaCountries).map(
                          (country) => ({
                            label: country,
                            value: country,
                          })
                        )}
                        rules={{ required: true }}
                        placeholder={t("Country")}
                        label={t("Country")}
                        isRequired
                        data-testid="add_sepa-country-input"
                      />
                    </FormGroup>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <FormGroup className="payment-form-group sm:col-span-2">
                      <FormInput
                        control={control}
                        name="field_passthrough4"
                        label={t("Nickname")}
                        data-testid="add_sepa-nickname-input"
                      />
                    </FormGroup>
                  </div>
                </div>
                {isValid && encryptedValues.length === 0 && (
                  <p role="alert" className="payment-error">
                    {t(
                      "The secure payment form could not be initialized. Please reload and try again."
                    )}
                  </p>
                )}
              </DrawerContent>
              <DrawerActions>
                <div className="w-full grow">
                  <Checkbox
                    label={t("Set as default payment method")}
                    subtitle={t(
                      isFirstPaymentMethod
                        ? "Your first payment method is automatically the default. Auto-Pay will stay unchanged."
                        : "Used for Auto-Pay when it is on. This does not turn Auto-Pay on."
                    )}
                    onChange={() =>
                      setIsDefaultPaymentChecked(!isDefaultPaymentChecked)
                    }
                    checked={isDefaultPaymentChecked}
                    data-testid="add_sepa-default-checkbox"
                    isDisabled={isFirstPaymentMethod}
                  />
                  <div className="payment-drawer-buttons">
                    <Button
                      type="button"
                      variant={ButtonVariant.Outline}
                      isDisabled={setup.isSubmitting}
                      onClick={setup.requestClose}
                    >
                      {t("Cancel")}
                    </Button>
                    <Button
                      className="font-hanken w-full sm:w-56 py-5"
                      variant={ButtonVariant.Primary}
                      type="submit"
                      isLoading={setup.isSubmitting}
                      isDisabled={
                        setup.isSubmitting ||
                        accountSignature == null ||
                        encryptedValues.length === 0
                      }
                    >
                      {t("Add Payment Method")}
                    </Button>
                  </div>
                </div>
              </DrawerActions>
            </>
          )}
        </form>
      )}
    </Drawer>
  );
}
